import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

interface Props {
  options: string[];
  value: string;
  placeholder?: string;
  onChange: (val: string) => void;
  className?: string;
}

const SearchableSelect: React.FC<Props> = ({ options, value, placeholder = 'Select', onChange, className }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = query
    ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
  };

  const handleOpen = () => {
    updatePosition();
    setOpen(true);
  };

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleScroll = () => updatePosition();
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
    setQuery('');
  };

  const dropdown = open ? ReactDOM.createPortal(
    <div ref={dropdownRef} className="ss-dropdown" style={dropdownStyle}>
      <div className="ss-search-wrap">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          ref={searchRef}
          className="ss-search"
          placeholder="Search..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <ul className="ss-list">
        <li
          className={`ss-option ${!value ? 'ss-option--selected' : ''}`}
          onMouseDown={() => handleSelect('')}
        >
          {placeholder}
        </li>
        {filtered.length === 0 ? (
          <li className="ss-option ss-option--empty">No results</li>
        ) : (
          filtered.map((opt) => (
            <li
              key={opt}
              className={`ss-option ${opt === value ? 'ss-option--selected' : ''}`}
              onMouseDown={() => handleSelect(opt)}
            >
              {opt}
            </li>
          ))
        )}
      </ul>
    </div>,
    document.body
  ) : null;

  return (
    <div className={`ss-wrap ${className || ''}`}>
      <button
        ref={triggerRef}
        type="button"
        className="ss-trigger"
        onClick={() => (open ? setOpen(false) : handleOpen())}
      >
        <span className={value ? 'ss-trigger__value' : 'ss-trigger__placeholder'}>
          {value || placeholder}
        </span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {dropdown}
    </div>
  );
};

export default SearchableSelect;
